/**
 * Responsibility: produce a single blended ground elevation for any world coordinate.
 *
 * This is the one authoritative answer to "how high is the ground at (x, z)". Tile
 * geometry, structure placement, and the flight model's terrain floor all ask here, so
 * they cannot disagree.
 *
 * ## How blending works
 *
 * Elevation is the weighted sum of every active biome's own elevation function. Because
 * each biome's function is continuous and the weights vary smoothly, the blend is also
 * continuous — mountains melt into flatland with no seam and no special-case transition
 * code.
 *
 * Contains no Three.js and no DOM access, so it is fully unit testable.
 */

import { BIOMES } from './biomes/index.js';
import { LATTICE_SPACING } from '../config.js';

/**
 * A weight provider that always reports a single biome at full strength.
 *
 * Used by the tile manager until the real biome field exists, and by tests that want one
 * biome in isolation. Task 6 replaces it with `world/biomeField.js`.
 *
 * @param {string} id - Biome id.
 * @returns {(x: number, z: number) => Array<{id: string, weight: number}>}
 */
export function constantWeights(id) {
  const weights = [{ id, weight: 1 }];
  return function sampleConstantWeights() {
    return weights;
  };
}

/**
 * Create a heightfield sampler.
 *
 * @param {object} options
 * @param {number} options.seed - Base world seed.
 * @param {(x: number, z: number) => Array<{id: string, weight: number}>} options.sampleWeights
 *   Returns the normalised biome weights at a coordinate. Weights are expected to sum to
 *   1; this module does not renormalise, because a provider whose weights do not sum to 1
 *   is a bug at the source and silently patching it here would hide it.
 * @param {Array<object>} [options.biomes] - Biome descriptors. Defaults to the registry.
 * @param {number} [options.latticeSpacing] - Spacing of the rendered lattice, used by
 *   `sampleSurface`. Defaults to the value derived from the tile config.
 * @returns {{sampleElevation: Function, sampleSurface: Function, sampleDetail: Function, getMaxElevation: Function}}
 */
export function createHeightfield({
  seed,
  sampleWeights,
  biomes = BIOMES,
  latticeSpacing = LATTICE_SPACING,
  deform = null,
}) {
  if (typeof sampleWeights !== 'function') {
    throw new Error('createHeightfield: sampleWeights must be a function');
  }
  if (deform !== null && typeof deform !== 'function') {
    throw new Error('createHeightfield: deform must be a function when given');
  }
  if (!(latticeSpacing > 0)) {
    throw new Error('createHeightfield: latticeSpacing must be positive');
  }

  /*
    Build each biome's elevation function once up front. These close over their own noise
    fields, which are relatively expensive to construct, so creating them per sample
    would be ruinous.
  */
  const elevationById = new Map();
  const biomeById = new Map();
  for (const biome of biomes) {
    elevationById.set(biome.id, biome.createElevation(seed));
    biomeById.set(biome.id, biome);
  }

  /** Highest elevation any registered biome can produce. */
  const maxElevation = biomes.reduce((peak, biome) => Math.max(peak, biome.maxElevation), 0);

  /**
   * Look up a biome's elevation function, failing loudly on an unknown id.
   * A missing id would otherwise contribute zero and read as an inexplicable flat patch.
   */
  function elevationFor(id) {
    const fn = elevationById.get(id);
    if (!fn) {
      throw new Error(`heightfield: no elevation function for biome id "${id}"`);
    }
    return fn;
  }

  /** Tallest elevation a single biome can reach, failing loudly on an unknown id. */
  function maxElevationFor(id) {
    const biome = biomeById.get(id);
    if (!biome) {
      throw new Error(`heightfield: no biome registered with id "${id}"`);
    }
    return biome.maxElevation;
  }

  /**
   * Blended elevation given weights that have already been sampled.
   *
   * Separated from `elevationAt` so `sampleDetail` can reuse the weights it needs anyway.
   * Fetching them twice per vertex doubled the cost of the hottest path in the project —
   * measured at roughly 4 ms per tile once three biomes were competing.
   */
  function elevationFromWeights(weights, x, z) {
    let elevation = 0;

    for (const { id, weight } of weights) {
      // Skip negligible contributions; sampling a noise field is the expensive part.
      if (weight <= 0) continue;
      elevation += elevationFor(id)(x, z) * weight;
    }

    /*
      **A hook for the one thing allowed to reshape the ground after the biomes have had their say**, and the
      single place blended elevation is produced — so applying it here is what makes it reach the drawn lattice,
      the structure placements, the depth mask and the colours alike. Anywhere else and only some of those would
      agree.

      It exists for a railway corridor. Everywhere else in this project structures sample terrain and never the
      reverse, and `airfieldGeometry` explains why a biome could not break that rule even if it wanted to:
      elevation is a weighted sum, so a contributor at 70% weight imposes 70% of its intent and 30% of its
      neighbour's. Applied *after* the sum, a corridor has no such problem — and it is the only way to get a
      cutting, since ground that has already been drawn cannot be drawn away.

      Null for the flying experience, where it costs one comparison per sample and nothing else.
    */
    return deform === null ? elevation : deform(x, z, elevation);
  }

  /**
   * Blended elevation of the continuous field.
   *
   * A local function rather than only a method, so `sampleSurface` can call it without
   * depending on `this` — the samplers are routinely passed around as bare functions, and
   * a detached method would silently break.
   */
  function elevationAt(x, z) {
    return elevationFromWeights(sampleWeights(x, z), x, z);
  }

  /*
    Direct-mapped cache of elevations at lattice points.

    ## Why a cache is safe here at all

    **Because the world is immutable.** Elevation is a pure function of `(seed, x, z)`, and this cache lives inside a
    heightfield built for one seed, so a stored value can never go stale. There is nothing to invalidate — which is the
    same property that lets a tile be thrown away and rebuilt identically.

    ## Why it is worth having

    Every structure in the world asks for the drawn surface, and each ask needs the four corners of a lattice cell. A
    forest tile places hundreds of trees, each sampling the ground several times, and they all land inside the same
    thousand-odd lattice cells — so the same corner was being recomputed dozens of times. One corner costs a biome-field
    sample plus up to three fBm evaluations, which is the most expensive arithmetic in the project.

    ## Why direct-mapped rather than a Map

    A `Map` needs a key, and a composite key means either a string per lookup or arithmetic that can collide silently.
    This is three typed arrays and a hash: no allocation per lookup, no rehashing, and a collision simply costs a
    recompute rather than a wrong answer, because the stored indices are checked. Bounded memory by construction, which a
    `Map` on an endless world would not be.
  */
  const CACHE_SLOTS = 1 << 13;
  const CACHE_MASK = CACHE_SLOTS - 1;

  const cacheX = new Int32Array(CACHE_SLOTS);
  const cacheZ = new Int32Array(CACHE_SLOTS);
  const cacheValue = new Float64Array(CACHE_SLOTS);
  const cacheFilled = new Uint8Array(CACHE_SLOTS);

  /**
   * Blended elevation at a lattice point, by index.
   *
   * @param {number} ix - Lattice index, so the coordinate is `ix * latticeSpacing`.
   * @param {number} iz
   * @returns {number}
   */
  function latticeElevation(ix, iz) {
    /*
      Two large odd multipliers xored together — the usual spatial hash. The multiplications overflow into doubles and
      `^` truncates to int32, which is deterministic in JavaScript, so two runs always agree about which slot a point
      uses. That only affects performance anyway: the indices below are what decide correctness.
    */
    const slot = ((ix * 73856093) ^ (iz * 19349663)) & CACHE_MASK;

    if (cacheFilled[slot] === 1 && cacheX[slot] === ix && cacheZ[slot] === iz) {
      return cacheValue[slot];
    }

    const value = elevationAt(ix * latticeSpacing, iz * latticeSpacing);

    cacheX[slot] = ix;
    cacheZ[slot] = iz;
    cacheValue[slot] = value;
    cacheFilled[slot] = 1;

    return value;
  }

  /**
   * Height and gradient of the drawn bilinear surface at a point.
   *
   * The single implementation behind both `sampleSurface` and `sampleSurfaceDetail`. Four corner
   * samples give the interpolated height and, from the same four numbers, the exact gradient of the
   * patch — so slope is free once height has been paid for.
   */
  function surfaceDetailAt(x, z) {
    // Lattice cell containing the point, held as indices so the four corners can be cached by index.
    const ix = Math.floor(x / latticeSpacing);
    const iz = Math.floor(z / latticeSpacing);

    const cellX = ix * latticeSpacing;
    const cellZ = iz * latticeSpacing;

    const fx = (x - cellX) / latticeSpacing;
    const fz = (z - cellZ) / latticeSpacing;

    const h00 = latticeElevation(ix, iz);
    const h10 = latticeElevation(ix + 1, iz);
    const h01 = latticeElevation(ix, iz + 1);
    const h11 = latticeElevation(ix + 1, iz + 1);

    const low = h00 + (h10 - h00) * fx;
    const high = h01 + (h11 - h01) * fx;

    /*
      Partial derivatives of the bilinear patch. The cross term is what makes a lattice cell a
      saddle rather than a plane, and it is why the gradient depends on where in the cell you are.
    */
    const cross = h00 - h10 - h01 + h11;
    const gradientX = (h10 - h00 + cross * fz) / latticeSpacing;
    const gradientZ = (h01 - h00 + cross * fx) / latticeSpacing;

    return {
      elevation: low + (high - low) * fz,
      slope: Math.hypot(gradientX, gradientZ),

      /*
        The gradient's *direction*, not just its magnitude. Free here — these are the same two numbers the
        slope is computed from — and it is what lets a biome lay something out along the land rather than on
        top of it: a contour runs perpendicular to the gradient, which is how ploughed rows actually follow
        a hillside.
      */
      gradientX,
      gradientZ,
    };
  }

  return {
    /**
     * Ground elevation of the continuous field, in world units.
     *
     * @param {number} x
     * @param {number} z
     * @returns {number}
     */
    sampleElevation(x, z) {
      return elevationAt(x, z);
    },

    /**
     * Elevation of the surface that is actually drawn, in world units.
     *
     * ## Why this is not the same as `sampleElevation`
     *
     * Terrain is drawn as a lattice of lines spaced `latticeSpacing` apart, so what the
     * player sees is the bilinear patch between four sampled corners — not the continuous
     * noise field. The two disagree between lattice points, and the field contains detail
     * finer than the lattice can show at all.
     *
     * Anything that has to agree with what is on screen must ask here. The flight model's
     * terrain floor is the case that matters: using the continuous field made the aircraft
     * climb over bumps too small to be drawn, and let it clip through ridge lines that are
     * drawn as straight segments between samples.
     *
     * `sampleElevation` remains the right call for generating the lattice itself, and for
     * anything that wants the true field rather than its rendered approximation.
     *
     * @param {number} x
     * @param {number} z
     * @returns {number}
     */
    sampleSurface(x, z) {
      return surfaceDetailAt(x, z).elevation;
    },

    /**
     * Elevation of the drawn surface *and* how steep it is there, from one set of samples.
     *
     * The gradient of a bilinear patch is already implicit in the four corners sampled to
     * interpolate its height, so asking for both costs exactly what asking for height alone costs.
     *
     * This exists because probing for slope separately was the single most expensive thing in the
     * project. Tree placement needs both, and sampling the surface three times per tree — once for
     * height, twice more to difference against — meant twelve blended elevation evaluations where
     * four would do. That was most of the 7 ms it took to build a forest tile.
     *
     * The slope returned is the true gradient of the surface that is drawn, not an approximation
     * from nearby probes, which could stray into a neighbouring lattice cell and report a slope from
     * the wrong patch.
     *
     * @param {number} x
     * @param {number} z
     * @returns {{elevation: number, slope: number}} `slope` is rise over run, always >= 0.
     */
    sampleSurfaceDetail(x, z) {
      return surfaceDetailAt(x, z);
    },

    /**
     * Elevation plus the information needed to colour it.
     *
     * Returned together because tile building needs both at every vertex, and asking
     * twice would sample the biome field and noise fields twice for no benefit.
     *
     * @param {number} x
     * @param {number} z
     * @returns {{elevation: number, normalized: number, weights: Array<{id: string, weight: number}>}}
     *   `normalized` is elevation mapped into 0..1 against the tallest possible biome,
     *   which is what the palette interpolates along.
     */
    sampleDetail(x, z) {
      const weights = sampleWeights(x, z);
      const elevation = elevationFromWeights(weights, x, z);

      /*
        Normalise against what the *present* biomes can reach, not against the tallest biome
        in the world.

        Colour is chosen by interpolating each biome's low-to-high gradient along this value,
        so dividing by a global maximum would mean a 210-unit baseline biome never exceeded
        0.23 and never showed anything but its darkest colour. Blending the maxima by the
        same weights as the elevation keeps the scale continuous across a transition, which a
        per-biome switch would not.
      */
      let localMax = 0;
      for (const { id, weight } of weights) {
        if (weight <= 0) continue;
        localMax += maxElevationFor(id) * weight;
      }

      return {
        elevation,
        normalized: localMax > 0 ? Math.min(1, Math.max(0, elevation / localMax)) : 0,
        weights,
      };
    },

    /** Highest elevation any registered biome can produce, in world units. */
    getMaxElevation() {
      return maxElevation;
    },
  };
}
